// Animation protocol — runtime side. Matches the fractal-fe editor's
// src/utils/animation.ts shape so the saved AnimationSettings produced by
// the AnimationPicker round-trip into framer-motion's `transition` object
// without translation at the renderer.

export type EasingKeyword =
  | 'linear'
  | 'ease'
  | 'ease_in'
  | 'ease_out'
  | 'ease_in_out'
  | 'spring'
  | 'bounce';

export type CubicBezierEasing = {
  type: 'cubic_bezier';
  points: [{ x: number; y: number }, { x: number; y: number }];
};

export type SpringEasing = {
  type: 'spring';
  stiffness: number;
  damping: number;
  mass: number;
};

export type EasingValue = EasingKeyword | CubicBezierEasing | SpringEasing;

export type AnimationType =
  | 'transform'
  | 'fade'
  | 'color_change'
  | 'movement'
  | 'rotate'
  | 'size'
  | 'auto';

export type AnimationRetain = 'end' | 'start' | 'auto';

// TNBEWEB-1969 — viewport-entry trigger config for the `init` entrance.
// Persisted at `style.states.init.animation.viewport`. When `enabled`, the
// renderer defers the init->Base flip until the element scrolls into view.
export type ViewportEntry = {
  enabled: boolean;
  threshold?: number; // 0..1 fraction of the element visible before triggering (default 0.2)
  rootMargin?: string; // IntersectionObserver rootMargin (default '0px')
  repeat?: boolean; // false = one-time (default); true = replay on each re-entry
};

export type AnimationSettings = {
  type?: AnimationType;
  delay: number; // ms
  duration: number; // ms
  easing: EasingValue;
  retain: AnimationRetain;
  viewport?: ViewportEntry;
};

export const isIdentityAnimation = (a: AnimationSettings | undefined | null): boolean => {
  if (!a) return true;
  // TNBEWEB-1969 — a viewport-triggered entrance must never be treated as
  // identity (which would disable `hasEntrance` in useInitEntrance), even if
  // its duration is 0. Keep in lockstep with fractal-fe.
  if (a.viewport?.enabled) return false;
  if (!a.duration || a.duration <= 0) return true;
  return false;
};

// Maps the protocol's EasingValue onto a CSS <easing-function> string for use in
// the `transition` shorthand. Keep this in sync with `toMotionTransition` (below)
// — both encode the same protocol vocabulary into different output formats
// (CSS string vs. framer-motion object). Used by `generateComponentStyles` so
// CSS-pseudo-class state transitions (:hover/:active) animate over the protocol's
// duration/easing rather than snapping instantly.
export const easingToCss = (e: EasingValue): string => {
  if (typeof e === 'string') {
    switch (e) {
      case 'linear': return 'linear';
      case 'ease': return 'ease';
      case 'ease_in': return 'ease-in';
      case 'ease_out': return 'ease-out';
      case 'ease_in_out': return 'ease-in-out';
      // Both treated as bouncy cubic-beziers until the v2 spring runtime lands.
      case 'spring': return 'cubic-bezier(0.5, 1.8, 0.5, 0.85)';
      case 'bounce': return 'cubic-bezier(0.68, -0.55, 0.27, 1.55)';
      default: return 'ease-out';
    }
  }
  if (e.type === 'cubic_bezier') {
    const [a, b] = e.points;
    // cubic-bezier requires the X control coords ∈ [0,1]; an out-of-range X
    // produces an illegal easing string that throws a synchronous TypeError
    // when applied as CSS / handed to Element.animate(). Defend here so a bad
    // persisted value can never crash the renderer. Y may overshoot, so only X
    // is clamped. NaN falls back to 0. (Mirrors the editor's TNBEWEB-1957 fix.)
    const clampX = (n: number) => (Number.isFinite(n) ? Math.max(0, Math.min(1, n)) : 0);
    const safeY = (n: number) => (Number.isFinite(n) ? n : 0);
    return `cubic-bezier(${clampX(a.x)}, ${safeY(a.y)}, ${clampX(b.x)}, ${safeY(b.y)})`;
  }
  if (e.type === 'spring') {
    // Fit spring(stiffness, damping, mass) to a cubic-bezier. Approximate
    // mapping: high stiffness + low damping → snappier, more overshoot.
    const stiffness = Math.max(1, e.stiffness);
    const damping = Math.max(0, e.damping);
    const ratio = damping / Math.sqrt(stiffness * (e.mass || 1) * 4);
    if (ratio >= 1) return 'cubic-bezier(0.4, 0, 0.2, 1)';
    if (ratio >= 0.7) return 'cubic-bezier(0.34, 1.2, 0.64, 1)';
    if (ratio >= 0.4) return 'cubic-bezier(0.34, 1.56, 0.64, 1)';
    return 'cubic-bezier(0.68, -0.55, 0.27, 1.55)';
  }
  return 'ease-out';
};

// Maps the protocol's EasingValue + duration/delay onto a framer-motion
// `transition` object. Framer-motion v12 accepts ease strings/arrays for
// tweens and the standard spring object for spring physics. Times are
// SECONDS in framer-motion; the protocol stores ms — convert here.
export const toMotionTransition = (a: AnimationSettings): {
  duration?: number;
  delay?: number;
  ease?: 'linear' | 'easeIn' | 'easeOut' | 'easeInOut' | [number, number, number, number];
  type?: 'spring' | 'tween';
  stiffness?: number;
  damping?: number;
  mass?: number;
} => {
  const durationSec = (a.duration || 0) / 1000;
  const delaySec = (a.delay || 0) / 1000;

  if (typeof a.easing === 'string') {
    switch (a.easing) {
      case 'linear':
        return { duration: durationSec, delay: delaySec, ease: 'linear' };
      case 'ease':
        return { duration: durationSec, delay: delaySec, ease: [0.25, 0.1, 0.25, 1] };
      case 'ease_in':
        return { duration: durationSec, delay: delaySec, ease: 'easeIn' };
      case 'ease_out':
        return { duration: durationSec, delay: delaySec, ease: 'easeOut' };
      case 'ease_in_out':
        return { duration: durationSec, delay: delaySec, ease: 'easeInOut' };
      case 'spring':
        return {
          type: 'spring', stiffness: 300, damping: 24, mass: 1, delay: delaySec,
        };
      case 'bounce':
        return { duration: durationSec, delay: delaySec, ease: [0.68, -0.55, 0.27, 1.55] };
      default:
        return { duration: durationSec, delay: delaySec, ease: 'easeOut' };
    }
  }

  if (a.easing.type === 'cubic_bezier') {
    const [p0, p1] = a.easing.points;
    return {
      duration: durationSec,
      delay: delaySec,
      ease: [p0.x, p0.y, p1.x, p1.y],
    };
  }

  if (a.easing.type === 'spring') {
    return {
      type: 'spring',
      stiffness: a.easing.stiffness,
      damping: a.easing.damping,
      mass: a.easing.mass || 1,
      delay: delaySec,
    };
  }

  return { duration: durationSec, delay: delaySec, ease: 'easeOut' };
};
